<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';
import {
  formatDay,
  formatPercent,
  percentOf,
} from '../../helpers/operationsChartHelper';

const props = defineProps({
  weeks: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n();

const plotRef = ref(null);
const { width } = useElementSize(plotRef);

const ROW_HEIGHT = 40;
const LABEL_WIDTH = 56;
const PADDING = { top: 8, right: 56, bottom: 22 };

// Ratings run 1..5; a neutral answer straddles the axis, half of it on each side.
const RATING_CLASSES = [
  'fill-n-ruby-11',
  'fill-n-ruby-9',
  'fill-n-slate-8',
  'fill-n-teal-9',
  'fill-n-teal-11',
];

const RATING_KEYS = ['VERY_BAD', 'BAD', 'NEUTRAL', 'GOOD', 'VERY_GOOD'];
const RATING_SWATCHES = [
  'bg-n-ruby-11',
  'bg-n-ruby-9',
  'bg-n-slate-8',
  'bg-n-teal-9',
  'bg-n-teal-11',
];

const answered = computed(() => props.weeks.filter(week => week.total > 0));

const chartWidth = computed(() => Math.max(width.value, 280));
const innerWidth = computed(
  () => chartWidth.value - LABEL_WIDTH - PADDING.right
);
const height = computed(
  () => PADDING.top + PADDING.bottom + ROW_HEIGHT * answered.value.length
);
const midpoint = computed(() => LABEL_WIDTH + innerWidth.value * 0.16);

const fractions = week =>
  [1, 2, 3, 4, 5].map(rating => (week.ratings[rating] || 0) / week.total);

// One scale for every row, so the rows stay comparable to each other.
const scale = computed(() => {
  let maxLeft = 0;
  let maxRight = 0;
  answered.value.forEach(week => {
    const parts = fractions(week);
    maxLeft = Math.max(maxLeft, parts[0] + parts[1] + parts[2] / 2);
    maxRight = Math.max(maxRight, parts[3] + parts[4] + parts[2] / 2);
  });

  return Math.min(
    (midpoint.value - LABEL_WIDTH) / (maxLeft || 1),
    (LABEL_WIDTH + innerWidth.value - midpoint.value) / (maxRight || 1)
  );
});

const rows = computed(() =>
  answered.value.map((week, index) => {
    const parts = fractions(week);
    const top = PADDING.top + ROW_HEIGHT * index + 10;
    let cursor =
      midpoint.value - (parts[0] + parts[1] + parts[2] / 2) * scale.value;

    const segments = parts.map((fraction, rating) => {
      const segmentWidth = fraction * scale.value;
      const segment = {
        key: `${week.timestamp}-${rating}`,
        x: cursor,
        width: Math.max(segmentWidth - 2, 0),
        class: RATING_CLASSES[rating],
        visible: segmentWidth > 0.5,
        tip: [
          `${t(`OPERATIONS_REPORTS.CSAT.RATINGS.${RATING_KEYS[rating]}`)} · ${formatDay(
            week.timestamp,
            locale.value,
            { day: 'numeric', month: 'short' }
          )}`,
          t('OPERATIONS_REPORTS.CSAT.TIP', {
            count: week.ratings[rating + 1] || 0,
            percent: formatPercent(fraction * 100, locale.value),
          }),
        ].join('\n'),
      };
      cursor += segmentWidth;
      return segment;
    });

    return {
      key: week.timestamp,
      y: top,
      label: formatDay(week.timestamp, locale.value, {
        day: 'numeric',
        month: 'short',
      }),
      segments,
      satisfaction: percentOf(
        (week.ratings[4] || 0) + (week.ratings[5] || 0),
        week.total
      ).toFixed(0),
    };
  })
);
</script>

<template>
  <div
    class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2 gap-1"
  >
    <div>
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('OPERATIONS_REPORTS.CSAT.TITLE') }}
      </h3>
      <p class="mt-0.5 mb-0 text-xs text-n-slate-11">
        {{ t('OPERATIONS_REPORTS.CSAT.SUBTITLE') }}
      </p>
    </div>

    <div class="flex flex-wrap items-center gap-3 mt-2 text-xs text-n-slate-11">
      <span
        v-for="(ratingKey, index) in RATING_KEYS"
        :key="`legend-${ratingKey}`"
        class="inline-flex items-center gap-1.5"
      >
        <i class="w-2.5 h-2.5 rounded-sm" :class="RATING_SWATCHES[index]" />
        {{ t(`OPERATIONS_REPORTS.CSAT.RATINGS.${ratingKey}`) }}
      </span>
    </div>

    <div ref="plotRef" class="w-full mt-2">
      <p
        v-if="!rows.length"
        class="py-10 mb-0 text-sm text-center text-n-slate-10"
      >
        {{ t('OPERATIONS_REPORTS.CSAT.EMPTY') }}
      </p>
      <svg
        v-else
        :viewBox="`0 0 ${chartWidth} ${height}`"
        :height="height"
        class="block w-full"
        role="img"
        :aria-label="t('OPERATIONS_REPORTS.CSAT.TITLE')"
      >
        <line
          :x1="midpoint"
          :y1="PADDING.top"
          :x2="midpoint"
          :y2="PADDING.top + ROW_HEIGHT * rows.length"
          class="stroke-n-strong"
          stroke-width="1"
        />

        <g v-for="row in rows" :key="row.key">
          <text x="0" :y="row.y + 14" font-size="11" class="fill-n-slate-10">
            {{ row.label }}
          </text>
          <template v-for="segment in row.segments" :key="segment.key">
            <rect
              v-if="segment.visible"
              :x="segment.x"
              :y="row.y"
              :width="segment.width"
              height="18"
              rx="3"
              :class="segment.class"
              :data-tip="segment.tip"
            />
          </template>
          <text
            :x="LABEL_WIDTH + innerWidth + 8"
            :y="row.y + 13"
            font-size="11"
            font-weight="600"
            class="fill-n-slate-12 tabular-nums"
          >
            {{ row.satisfaction }}%
          </text>
        </g>

        <text
          :x="midpoint - 6"
          :y="height - 6"
          text-anchor="end"
          font-size="10"
          class="fill-n-slate-10"
        >
          {{ t('OPERATIONS_REPORTS.CSAT.UNHAPPY') }}
        </text>
        <text
          :x="midpoint + 6"
          :y="height - 6"
          font-size="10"
          class="fill-n-slate-10"
        >
          {{ t('OPERATIONS_REPORTS.CSAT.HAPPY') }}
        </text>
      </svg>
    </div>
  </div>
</template>
